import { arrayUtil } from "@sdeapps/react-core";
import type { Perimetre, PerimetreSansTerritoire, Transfert, TransfertCEA } from "models";

/**
 * Récupère une chaîne de charactères représentant simplement les propriétés suivantes d'un
 * transfert :
 *
 * - type de transfert
 * - code de compétence / sous-compétence / portée
 * - bassin versant
 *
 * Dans le but de pouvoir simplement comparer ou ordonner plusieurs transferts entre eux.
 * @param transfert le transfert
 * @returns une `string` représentant les propriétés concernées du transfert
 */
function getTransfertsComparisonBase(transfert: Transfert): string {
  return `${transfert.idPerimetre}${transfert?.idEpci}${transfert.bassinVersant}${transfert.code}`;
}

/**
 * Récupère une chaîne de charactères représentant simplement les propriétés suivantes d'un
 * transfert :
 *
 * - périmètre
 * - type de transfert
 * - code de compétence / sous-compétence / portée
 * - bassin versant
 * - date de délibération
 * - date de début de transfert
 *
 * Dans le but de pouvoir simplement comparer ou ordonner plusieurs transferts entre eux.
 * @param sousCompetence le transfert
 * @returns une `string` représentant les propriétés concernées du transfert
 */
function getTransfertsComparisonBaseWithDates(transfert: Transfert): string {
  return `${getTransfertsComparisonBase(transfert)}${transfert.dateDeliberation}${transfert.dateDebut}`;
}

/**
 * Récupère une chaîne de charactères représentant simplement les propriétés suivantes d'un
 * transfert spécial (sans Territoire) :
 *
 * - périmètre
 * - entité transférante
 * - lieu particulier
 * - code de compétence / sous-compétence / portée
 *
 * Dans le but de pouvoir simplement comparer ou ordonner plusieurs transferts entre eux.
 * @param transfert le transfert
 * @returns une `string` représentant les propriétés concernées du transfert
 */
function getTransfertsSansTerritoireComparisonBase(transfert: TransfertCEA): string {
  return `${transfert.idPerimetre}${transfert.entiteTransferante.id}${transfert.lieuParticulier.id}${transfert.code}`;
}

/**
 * Récupère une chaîne de charactères représentant simplement les propriétés suivantes d'un
 * transfert spécial (sans Territoire) :
 *
 * - périmètre
 * - code de compétence / sous-compétence / portée
 * - l'entité transférante
 * - le lieu particulier
 * - date de délibération
 * - date de début de transfert
 *
 * Dans le but de pouvoir simplement comparer ou ordonner plusieurs transferts entre eux.
 * @param sousCompetence le transfert
 * @returns une `string` représentant les propriétés concernées du transfert
 */
function getTransfertsSansTerritoireComparisonBaseWithDates(transfert: TransfertCEA): string {
  return `${getTransfertsSansTerritoireComparisonBase(transfert)}${transfert.dateDeliberation}${transfert.dateDebut}`;
}

/**
 * Groupe les transferts d'un périmètre, d'abord par communes, puis par :
 *
 * - type de transfert
 * - code de compétence / sous-compétence / portée
 * - bassin versant
 *
 * **Attention** déduplique également les transferts par code de transfert pour chaque Commune, ce qui
 * influe sur le regroupement.
 *
 * @param transferts la liste des `Transfert` que l'on veut regrouper
 * @returns une liste de listes de listes de `Transfert`. La première liste de transfert représente une Commune,
 * on a donc la liste de ceci qui est la liste de toutes les communes, puis une liste de celles-ci qui regroupe les
 * communes qui ont des transferts équivalents selon {@link getTransfertsComparisonBase}.
 */
function getGroupedPerimetreCommunes(transferts: Array<Transfert>): Array<Array<Array<Transfert>>> {
  const transfertsByCommune = arrayUtil.groupByArray(
    transferts,
    (t) => `${t.idCommune}${t.idEpci}`
  );

  // dedup by transfert code + bassin versant inside commune
  const transfertsByCommuneDedup = transfertsByCommune.map((communeTransferts) =>
    arrayUtil.dedupArray(communeTransferts, (t) => `${t.code}${t.bassinVersant}`)
  );

  const transfertsByCommuneByPortees = arrayUtil.groupByArray(
    transfertsByCommuneDedup,
    (_transfertsByCommune) => {
      const comparisonString = _transfertsByCommune
        .toSorted((a, b) =>
          getTransfertsComparisonBase(a).localeCompare(getTransfertsComparisonBase(b))
        )
        .reduce<string>((acc, transfert) => {
          acc += getTransfertsComparisonBase(transfert);
          return acc;
        }, "");

      return comparisonString;
    }
  );

  return transfertsByCommuneByPortees;
}

/**
 * Groupe les transferts d'un périmètre, d'abord par lieu particulier, puis par :
 *
 * - entité transférante
 * - code de compétence / sous-compétence / portée
 * - bassin versant
 *
 * **Attention** déduplique également les transferts par code de transfert pour chaque Commune, ce qui
 * influe sur le regroupement.
 *
 * @param transferts la liste des `Transfert` que l'on veut regrouper
 * @returns une liste de listes de listes de `Transfert`. La première liste de transfert représente une Commune,
 * on a donc la liste de ceci qui est la liste de toutes les communes, puis une liste de celles-ci qui regroupe les
 * communes qui ont des transferts équivalents selon {@link getTransfertsComparisonBase}.
 */
function getGroupedPerimetreLieuxParticuliers(
  transferts: Array<TransfertCEA>
): Array<Array<Array<TransfertCEA>>> {
  const transfertsByLieuParticulier = arrayUtil.groupByArray(
    transferts,
    (t) => `${t.entiteTransferante.id}${t.lieuParticulier.id}`
  );

  const transfertsByCommuneByPortees = arrayUtil.groupByArray(
    transfertsByLieuParticulier,
    (_transfertsByCommune) => {
      const comparisonString = _transfertsByCommune
        .toSorted((a, b) =>
          getTransfertsSansTerritoireComparisonBase(a).localeCompare(
            getTransfertsSansTerritoireComparisonBase(b)
          )
        )
        .reduce<string>((acc, transfert) => {
          acc += getTransfertsSansTerritoireComparisonBase(transfert);
          return acc;
        }, "");

      return comparisonString;
    }
  );

  return transfertsByCommuneByPortees;
}

/**
 * Groupe les transferts d'un périmètre, d'abord par lieu particulier, puis par :
 *
 * - entité transférante
 * - code de compétence / sous-compétence / portée
 * - bassin versant
 * - date de délibération
 * - date de début de transfert
 *
 * @param transferts la liste des `Transfert` que l'on veut regrouper
 * @returns une liste de listes de listes de `Transfert`. La première liste de transfert représente une Commune,
 * on a donc la liste de ceci qui est la liste de toutes les communes, puis une liste de celles-ci qui regroupe les
 * communes qui ont des transferts équivalents selon {@link getTransfertsComparisonBaseWithDates}.
 */
function getGroupedPerimetreCommunesWithDates(
  transferts: Array<Transfert>
): Array<Array<Array<Transfert>>> {
  const transfertsByCommune = arrayUtil.groupByArray(
    transferts,
    (t) => `${t.idCommune}${t.idEpci}`
  );

  const transfertsByCommuneByPortees = arrayUtil.groupByArray(
    transfertsByCommune,
    (_transfertsByCommune) => {
      const comparisonString = _transfertsByCommune
        .toSorted((a, b) =>
          getTransfertsComparisonBaseWithDates(a).localeCompare(
            getTransfertsComparisonBaseWithDates(b)
          )
        )
        .reduce<string>((acc, transfert) => {
          acc += getTransfertsComparisonBaseWithDates(transfert);
          return acc;
        }, "");

      return comparisonString;
    }
  );

  return transfertsByCommuneByPortees;
}

/**
 * Groupe les transferts d'un périmètre, d'abord par communes, puis par :
 *
 * - type de transfert
 * - code de compétence / sous-compétence / portée
 * - bassin versant
 * - date de délibération
 * - date de début de transfert
 *
 * @param transferts la liste des `Transfert` que l'on veut regrouper
 * @returns une liste de listes de listes de `Transfert`. La première liste de transfert représente une Commune,
 * on a donc la liste de ceci qui est la liste de toutes les communes, puis une liste de celles-ci qui regroupe les
 * communes qui ont des transferts équivalents selon {@link getTransfertsComparisonBaseWithDates}.
 */
function getGroupedPerimetreSansTerritoireLieuxParticuliersWithDates(
  transferts: Array<TransfertCEA>
): Array<Array<Array<TransfertCEA>>> {
  const transfertsByLieuParticulier = arrayUtil.groupByArray(
    transferts,
    (t) => `${t.entiteTransferante.id}${t.lieuParticulier.id}`
  );

  const transfertsByLieuParticulierByPortees = arrayUtil.groupByArray(
    transfertsByLieuParticulier,
    (_transfertsByLieuParticulier) => {
      const comparisonString = _transfertsByLieuParticulier
        .toSorted((a, b) =>
          getTransfertsSansTerritoireComparisonBaseWithDates(a).localeCompare(
            getTransfertsSansTerritoireComparisonBaseWithDates(b)
          )
        )
        .reduce<string>((acc, transfert) => {
          acc += getTransfertsSansTerritoireComparisonBaseWithDates(transfert);
          return acc;
        }, "");

      return comparisonString;
    }
  );

  return transfertsByLieuParticulierByPortees;
}

function isPerimetreAvecTerritoire(
  perimetre: Perimetre | PerimetreSansTerritoire
): perimetre is Perimetre {
  return perimetre.territoire != null;
}

function isPerimetreSansTerritoire(
  perimetre: Perimetre | PerimetreSansTerritoire
): perimetre is PerimetreSansTerritoire {
  return perimetre.territoire == null;
}

export const perimetresUtil = {
  getGroupedPerimetreCommunes,
  getGroupedPerimetreCommunesWithDates,
  getGroupedPerimetreLieuxParticuliers,
  getGroupedPerimetreSansTerritoireLieuxParticuliersWithDates,
  isPerimetreAvecTerritoire,
  isPerimetreSansTerritoire,
};
